import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../LanguageContext';
import backgroundImage from '../assets/images/usina-de-cima.jpeg';

// Animation for the recycling icon (simple pulse)
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

const moveBackground = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const HeroContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80vh;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, 
    rgba(0, 128, 0, 0.85) 0%, 
    rgba(0, 174, 239, 0.85) 100%
  ), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
      ${({ theme }) => theme.colors.greenPrimary}88 0%,
      ${({ theme }) => theme.colors.blueLight}88 100%
    );
    background-size: 200% 200%;
    animation: ${moveBackground} 15s ease infinite;
    z-index: 1;
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

const Headline = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.primary};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 3.5rem;
  }
`;

const Subheadline = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  max-width: 600px;
  font-weight: 400;
  font-family: ${({ theme }) => theme.fonts.primary};
`;

const CTAButton = styled.a`
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.greenPrimary};
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 30px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transition: background-color 0.3s ease, color 0.3s ease;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.blueLight};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const RecyclingIcon = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  animation: ${pulse} 3s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.greenPrimary};
  font-size: 2rem;
  user-select: none;
`;

// Placeholder SVG or emoji for recycling icon
const RecyclingSymbol = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 24 24"
    width="32"
    height="32"
    aria-label="Índice de resíduos reciclados"
  >
    <path d="M12 2L8 6h3v6h2V6h3l-4-4zm-6 8v6h2v-4h4v4h2v-6H6zm12 0v6h-2v-4h-4v4h-2v-6h8z" />
  </svg>
);

const HeroSection = () => {
  const { translations } = useLanguage();

  return (
    <HeroContainer>
      <Headline>{translations.hero.headline}</Headline>
      <Subheadline>{translations.hero.subheadline}</Subheadline>
      <CTAButton 
        href="https://wa.me/5562981308130" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {translations.hero.cta}
      </CTAButton>
      <RecyclingIcon aria-label={translations.hero.recyclingIconLabel}>
        <RecyclingSymbol />
      </RecyclingIcon>
    </HeroContainer>
  );
};

export default HeroSection;
